import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { parseEmployeeSheet, templateCsv } from "./employeeImport";
import { PrimaryButton, SecondaryButton } from "./hrComponents";
import { resetDirectoryCache } from "./EmployeeSelect";

// EMP-06: upload, preview every row (nothing is created yet), then import. The import runs in the
// background; this panel follows the job until it finishes.
const STEP = { PICK: "pick", PREVIEW: "preview", RUNNING: "running", DONE: "done" };

export default function EmployeeImportDrawer({ show, onHide, onDone }) {
  const [step, setStep] = useState(STEP.PICK);
  const [columns, setColumns] = useState([]);
  const [maxRows, setMaxRows] = useState(500);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!show) return undefined;
    setStep(STEP.PICK);
    setRows([]);
    setPreview(null);
    setJob(null);
    hrmsService
      .getImportTemplate()
      .then((res) => {
        setColumns(res?.data?.columns || []);
        setMaxRows(res?.data?.max_rows || 500);
      })
      .catch(() => {});
    return () => clearTimeout(timer.current);
  }, [show]);

  const downloadTemplate = () => {
    const blob = new Blob([templateCsv(columns)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "employee-import-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const parsed = parseEmployeeSheet(await file.arrayBuffer(), columns, maxRows);
      const res = await hrmsService.previewImport(parsed);
      if (!res?.success) return toast.error(hrmsService.responseMessage(res));
      setRows(parsed);
      setPreview(res.data);
      setStep(STEP.PREVIEW);
    } catch (e) {
      toast.error(e.message || "Could not read the file");
    } finally {
      setBusy(false);
    }
  };

  const poll = (id) => {
    hrmsService
      .getJob(id)
      .then((res) => {
        const j = res?.data;
        setJob(j);
        if (j && (j.status === "DONE" || j.status === "FAILED")) {
          setStep(STEP.DONE);
          resetDirectoryCache(); // even a partial/failed run may have added some rows before failing
          onDone && onDone();
        } else timer.current = setTimeout(() => poll(id), 1500);
      })
      .catch(() => {
        timer.current = setTimeout(() => poll(id), 3000);
      });
  };

  const start = async () => {
    setBusy(true);
    try {
      const res = await hrmsService.commitImport(rows);
      if (!res?.success) return toast.error(hrmsService.responseMessage(res));
      setStep(STEP.RUNNING);
      poll(res.data.job_id);
    } finally {
      setBusy(false);
    }
  };

  const summary = preview?.summary;
  const bad = (preview?.rows || []).filter((r) => !r.ok);

  const footer =
    step === STEP.PREVIEW ? (
      <>
        <SecondaryButton onClick={() => setStep(STEP.PICK)} disabled={busy}>
          Choose another file
        </SecondaryButton>
        <PrimaryButton onClick={start} disabled={busy || !summary || summary.invalid > 0}>
          Import {summary ? summary.valid : 0} employees
        </PrimaryButton>
      </>
    ) : (
      <SecondaryButton onClick={onHide} disabled={step === STEP.RUNNING}>
        Close
      </SecondaryButton>
    );

  return (
    <SlidePanel show={show} onHide={() => step !== STEP.RUNNING && onHide()} title="Import employees" width={600} footer={footer}>
      {step === STEP.PICK ? (
        <>
          <p style={{ fontSize: 14 }}>
            Fill in the template (up to {maxRows} people), then upload it. You will see every row checked before anything is created. Enter dates as 2026-01-15 or 15 Jan 2026, and write departments, designations and other masters exactly as they are set up.
          </p>
          <button type="button" className="btn btn-outline-secondary mb-3" onClick={downloadTemplate} disabled={!columns.length}>
            Download template
          </button>
          <input type="file" className="form-control" accept=".csv,.xlsx,.xls" onChange={(e) => pick(e.target.files[0])} disabled={busy || !columns.length} />
        </>
      ) : null}

      {step === STEP.PREVIEW && preview ? (
        <>
          <div style={{ fontSize: 14, marginBottom: 12 }}>
            {summary.total} rows: <strong style={{ color: "#065F46" }}>{summary.valid} ready</strong>
            {summary.invalid ? <>, <strong style={{ color: "#B91C1C" }}>{summary.invalid} with problems</strong></> : null}
          </div>
          {bad.length ? (
            <div style={{ fontSize: 13 }}>
              {bad.map((r) => (
                <div key={r.row_no} style={{ padding: "8px 0", borderBottom: "1px solid #F3F4F6" }}>
                  <strong>Row {r.row_no}</strong> {r.full_name ? `(${r.full_name})` : ""}
                  {r.errors.map((e) => (
                    <div key={e} style={{ color: "#B91C1C" }}>
                      {e}
                    </div>
                  ))}
                </div>
              ))}
              <div style={{ color: "#6B7280", marginTop: 8 }}>Fix these in the file and upload it again.</div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#065F46" }}>Every row looks good.</div>
          )}
        </>
      ) : null}

      {step === STEP.RUNNING ? (
        <div style={{ fontSize: 14 }}>
          Importing… {job ? `${job.progress || 0}%` : ""}
          <div style={{ fontSize: 12, color: "#6B7280" }}>{job?.progress_message}</div>
        </div>
      ) : null}

      {step === STEP.DONE ? (
        <div style={{ fontSize: 14 }}>
          {job?.status === "DONE" ? (
            <>
              <div style={{ color: "#065F46", fontWeight: 600 }}>Import finished: {job.result?.created ?? 0} employees added.</div>
              {(job.result?.failed || []).map((f) => (
                <div key={f.row_no} style={{ color: "#B91C1C", fontSize: 13, marginTop: 6 }}>
                  Row {f.row_no}: {f.message}
                </div>
              ))}
            </>
          ) : (
            <div style={{ color: "#B91C1C" }}>The import could not be completed. Nothing more was added; try again or contact support.</div>
          )}
        </div>
      ) : null}
    </SlidePanel>
  );
}
